/**
 * WI 34280: per-seat Inbound Queue Depth, ported from the legacy Control Center
 * overflow-queue widget (PR 1398) for the Roost wallboard. Pure and Angular-free
 * so the web wallboard and the iPad wallboard (WI 34539) share one set of rules.
 *
 * Source: GET /api/ryn-db/open-inbound-depths on the bridge. The envelope is
 * lowercase ({ count, depths }) but each depth row is PascalCase on the wire
 * (WriteAsJsonAsync with no naming policy); the legacy widget typed the rows as
 * camelCase and would read undefined against the live endpoint.
 */

/** One row of `depths` from open-inbound-depths, as sent. */
export interface InboundDepthWireRow {
  InstanceId: string;
  /** Open rows still needing a response (parked included). */
  OpenCount: number | string;
  /** Unparked rows whose SlaDueAt has passed (bridge per-channel SLA, 5 min for Teams). */
  OverdueCount: number | string;
  ParkedCount: number | string;
  /** Earliest SlaDueAt among the seat's unparked rows; null when all are parked. */
  OldestSlaDueAt: string | null;
}

/**
 * overdue: at least one unparked row past SLA.
 * active: open rows being worked, none past SLA.
 * parked: every open row is parked with a reason.
 * clear: nothing open.
 */
export type InboundQueueBand = 'overdue' | 'parked' | 'active' | 'clear';

export interface InboundQueueRow {
  instanceId: string;
  openCount: number;
  overdueCount: number;
  parkedCount: number;
  oldestSlaDueAt: string | null;
  band: InboundQueueBand;
  /**
   * Minutes the oldest pending unparked SLA is PAST due (zero or positive) or
   * until due (negative, rounded up so "due in 1m" never reads as overdue).
   * null when the seat has no unparked row.
   */
  oldestSlaMinutes: number | null;
}

export interface InboundQueueSummary {
  totalOpen: number;
  totalOverdue: number;
  totalParked: number;
  seatsOverdue: number;
  /** Head of the busiest-first list; null when nothing is open. */
  busiestSeat: string | null;
}

/** Rows busiest first: most open, then most overdue, then seat id (ordinal). */
export function buildInboundQueueRows(
  depths: readonly InboundDepthWireRow[] | null | undefined,
  nowMs: number
): InboundQueueRow[] {
  return (depths ?? [])
    .map((d) => toRow(d, nowMs))
    .sort(
      (a, b) =>
        b.openCount - a.openCount ||
        b.overdueCount - a.overdueCount ||
        compareOrdinal(a.instanceId, b.instanceId)
    );
}

export function summarizeInboundQueue(
  rows: readonly InboundQueueRow[]
): InboundQueueSummary {
  const open = rows.filter((r) => r.openCount > 0);
  return {
    totalOpen: rows.reduce((sum, r) => sum + r.openCount, 0),
    totalOverdue: rows.reduce((sum, r) => sum + r.overdueCount, 0),
    totalParked: rows.reduce((sum, r) => sum + r.parkedCount, 0),
    seatsOverdue: rows.filter((r) => r.overdueCount > 0).length,
    busiestSeat: open.length > 0 ? open[0].instanceId : null,
  };
}

/** "5m overdue", "due in 3m", or "" when the seat has no unparked row. */
export function slaAgeText(oldestSlaMinutes: number | null): string {
  if (oldestSlaMinutes === null) return '';
  if (oldestSlaMinutes >= 0)
    return `${formatMinutes(oldestSlaMinutes)} overdue`;
  return `due in ${formatMinutes(-oldestSlaMinutes)}`;
}

/** One-line flock summary for the card header. */
export function inboundQueueSummaryText(s: InboundQueueSummary): string {
  if (s.totalOpen === 0) return 'All inbound queues clear';
  const seats = `${s.seatsOverdue} ${s.seatsOverdue === 1 ? 'seat' : 'seats'}`;
  const busiest = s.busiestSeat ? ` · busiest ${s.busiestSeat}` : '';
  return `${s.totalOpen} open · ${s.totalOverdue} overdue (${seats}) · ${s.totalParked} parked${busiest}`;
}

/** Text alternative for a row's band dot, so the band is not colour-only. */
export function inboundQueueBandLabel(band: InboundQueueBand): string {
  switch (band) {
    case 'overdue':
      return 'Overdue';
    case 'parked':
      return 'All parked';
    case 'active':
      return 'Active, within SLA';
    default:
      return 'Clear';
  }
}

function toRow(d: InboundDepthWireRow, nowMs: number): InboundQueueRow {
  const openCount = count(d.OpenCount);
  const parkedCount = count(d.ParkedCount);
  const due = d.OldestSlaDueAt ? Date.parse(d.OldestSlaDueAt) : NaN;
  // The server counts OverdueCount once, when the depths are read, and depths
  // are only re-read on an inbound change or the 5-minute reconcile. A deadline
  // that has passed since then is overdue NOW, so the live clock decides too:
  // the band, the row count and the summary stay in step with the "Nm overdue"
  // text on every tick. OldestSlaDueAt covers unparked rows only, so a passed
  // deadline always means at least one unparked row is overdue.
  const liveOverdue = !Number.isNaN(due) && due <= nowMs;
  const overdueCount = Math.max(count(d.OverdueCount), liveOverdue ? 1 : 0);
  return {
    instanceId: d.InstanceId,
    openCount,
    overdueCount,
    parkedCount,
    oldestSlaDueAt: d.OldestSlaDueAt,
    band: classify(openCount, overdueCount, parkedCount),
    oldestSlaMinutes: Number.isNaN(due) ? null : slaMinutes(nowMs, due),
  };
}

/** Whole minutes past due (floored), or until due as a negative (ceiled). */
function slaMinutes(nowMs: number, dueMs: number): number {
  return nowMs >= dueMs
    ? Math.floor((nowMs - dueMs) / 60_000)
    : -Math.ceil((dueMs - nowMs) / 60_000);
}

function classify(
  openCount: number,
  overdueCount: number,
  parkedCount: number
): InboundQueueBand {
  if (overdueCount > 0) return 'overdue';
  if (openCount <= 0) return 'clear';
  if (parkedCount >= openCount) return 'parked';
  return 'active';
}

/** The legacy payload sometimes carried counts as strings; bad values read 0. */
function count(v: number | string): number {
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function formatMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ${String(min % 60).padStart(2, '0')}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function compareOrdinal(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
